import { useState, useEffect } from 'react';
import { parkingApi, serviceChargeApi, productsApi } from '../services/api';
import { formatCurrency, formatDuration, formatDateTime, formatApiError } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import { X, Check, Percent, Gift, CurrencyCircleDollar, Clock, Receipt, Warning, Trash, Plus, ShoppingCart, Minus } from '@phosphor-icons/react';

export default function CheckOutModal({ space, vehicleTypes, onClose, onComplete, onVoidComplete }) {
  const { user, isAdmin } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isCourtesy, setIsCourtesy] = useState(false);
  const [customAmount, setCustomAmount] = useState(null);
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [serviceCharges, setServiceCharges] = useState([]);

  // Agregar cargo flow
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [chargeStep, setChargeStep] = useState(1); // 1=product, 2=quantity, 3=mode
  const [chargeLoading, setChargeLoading] = useState(false);

  useEffect(() => {
    loadPreview();
    loadProducts();
  }, []);

  useEffect(() => {
    loadPreview();
  }, [discount, isCourtesy, customAmount]);

  const loadProducts = async () => {
    try {
      const res = await productsApi.getAll(true);
      setProducts(res.data);
    } catch (err) { console.error('Error loading products:', err); }
  };

  const loadPreview = async () => {
    try {
      const response = await parkingApi.checkOutPreview({
        space_id: space.id, discount, is_courtesy: isCourtesy, custom_amount: customAmount
      });
      setPreview(response.data);
      setServiceCharges(response.data.service_charges || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');
    try {
      const response = await parkingApi.checkOutConfirm({
        space_id: space.id, discount, is_courtesy: isCourtesy, custom_amount: customAmount
      });
      onComplete(response.data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setConfirming(false); }
  };

  const handleVoidSession = async () => {
    setVoiding(true);
    setError('');
    try {
      await parkingApi.voidSession({ space_id: space.id, reason: 'operator_error' });
      setShowVoidConfirm(false);
      if (onVoidComplete) onVoidComplete();
      else onClose();
    } catch (err) { setError(formatApiError(err)); }
    finally { setVoiding(false); }
  };

  // ── Add Charge Flow ──
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setChargeStep(2); // Go to quantity step
  };

  const handleSelectPaymentMode = async (paymentMode) => {
    if (!selectedProduct || quantity < 1) return;
    setChargeLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await serviceChargeApi.add({
        space_id: space.id,
        product_id: selectedProduct.id,
        payment_mode: paymentMode,
        quantity: quantity
      });
      setSuccess(response.data.message);
      setShowAddCharge(false);
      setSelectedProduct(null);
      setQuantity(1);
      setChargeStep(1);
      await loadPreview();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(formatApiError(err)); }
    finally { setChargeLoading(false); }
  };

  const handleVoidCharge = async (chargeId) => {
    if (!isAdmin) { setError('Solo administradores pueden anular cargos'); return; }
    const reason = prompt('Razon de la anulacion:');
    if (!reason) return;
    try {
      await serviceChargeApi.void({ charge_id: chargeId, reason });
      setSuccess('Cargo anulado correctamente');
      await loadPreview();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(formatApiError(err)); }
  };

  const handleCourtesy = () => { setIsCourtesy(true); setDiscount(0); setCustomAmount(null); setShowAdjustments(false); };
  const handleCustomAmount = () => { const a = prompt('Ingrese monto personalizado:'); if (a && !isNaN(a)) { setCustomAmount(parseInt(a)); setIsCourtesy(false); setDiscount(0); setShowAdjustments(false); } };
  const handleDiscount = () => { const a = prompt('Ingrese monto de descuento:'); if (a && !isNaN(a)) { setDiscount(parseInt(a)); setIsCourtesy(false); setCustomAmount(null); setShowAdjustments(false); } };
  const resetAdjustments = () => { setDiscount(0); setIsCourtesy(false); setCustomAmount(null); setShowAdjustments(false); };

  // Calculate extras summary with quantities
  const activeCharges = serviceCharges.filter(c => !c.voided);
  const paidCharges = activeCharges.filter(c => c.payment_mode === 'paid_now');
  const pendingCharges = activeCharges.filter(c => c.payment_mode === 'add_to_total');
  const paidAmount = paidCharges.reduce((sum, c) => sum + (c.amount || 0), 0);
  const pendingAmount = pendingCharges.reduce((sum, c) => sum + (c.amount || 0), 0);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
        <div className="bg-[#141414] rounded-xl p-8"><div className="text-white text-lg">Cargando...</div></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 overflow-y-auto" data-testid="checkout-modal">
      <div className="bg-[#141414] rounded-xl w-full max-w-lg border border-[#27272A] my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
          <div>
            <h2 className="text-xl font-heading text-white">Salida - Espacio {space.label}</h2>
            <p className="text-sm text-[#A1A1AA]">Confirme el cobro</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#27272A] rounded-lg transition-colors" data-testid="checkout-close">
            <X className="w-6 h-6 text-[#A1A1AA]" weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {error && <div className="bg-[#450A0A] border border-[#DC2626] text-[#FCA5A5] px-4 py-3 rounded-lg" data-testid="checkout-error">{error}</div>}
          {success && <div className="bg-[#052E16] border border-[#16A34A] text-[#4ADE80] px-4 py-3 rounded-lg flex items-center gap-2" data-testid="checkout-success"><Check className="w-5 h-5" weight="bold" />{success}</div>}

          {preview && (
            <>
              {/* Vehicle Info */}
              <div className="bg-[#1C1C1E] rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[#A1A1AA] text-sm uppercase tracking-wide">Patente</span>
                  <span className="font-mono text-2xl text-white tracking-[0.1em]" data-testid="checkout-plate">{preview.plate_number}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#A1A1AA] text-sm uppercase tracking-wide">Tipo</span>
                  <span className="text-white">{preview.vehicle_type}</span>
                </div>
              </div>

              {/* Agregar cargo Button */}
              <button onClick={() => { setShowAddCharge(true); setChargeStep(1); setSelectedProduct(null); setQuantity(1); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#1e3a5f] border border-[#60A5FA]/30 rounded-lg hover:bg-[#1e4a7f] transition-colors"
                data-testid="add-charge-btn">
                <Plus className="w-5 h-5 text-[#60A5FA]" weight="bold" />
                <span className="text-[#60A5FA] font-medium">Agregar cargo</span>
              </button>

              {/* Extras Summary */}
              {activeCharges.length > 0 && (
                <div className="bg-[#1C1C1E] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingCart className="w-5 h-5 text-[#60A5FA]" weight="bold" />
                    <span className="text-white font-medium">Extras</span>
                  </div>
                  <div className="space-y-2">
                    {paidAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#4ADE80]">Cobrado:</span>
                        <span className="text-[#4ADE80] font-mono">{formatCurrency(paidAmount)}</span>
                      </div>
                    )}
                    {pendingAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#FCD34D]">Pendiente:</span>
                        <span className="text-[#FCD34D] font-mono">{formatCurrency(pendingAmount)}</span>
                      </div>
                    )}
                    <div className="mt-2 pt-2 border-t border-[#27272A] space-y-1">
                      {activeCharges.map((charge) => {
                        const qty = charge.quantity || 1;
                        const name = charge.item_name || charge.service_type || 'Extra';
                        const label = qty > 1 ? `${name} x${qty}` : name;
                        return (
                          <div key={charge.id} className="flex items-center justify-between text-xs">
                            <span className="text-[#A1A1AA]">
                              {label} — {charge.payment_mode === 'paid_now' ? 'cobrado' : 'pendiente'}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-mono">{formatCurrency(charge.amount)}</span>
                              {isAdmin && (
                                <button onClick={() => handleVoidCharge(charge.id)} className="p-1 hover:bg-[#450A0A] rounded text-[#FCA5A5]" data-testid={`void-charge-${charge.id}`}>
                                  <X className="w-3 h-3" weight="bold" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Time Info */}
              <div className="bg-[#1C1C1E] rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#A1A1AA] text-sm">Entrada</span>
                  <span className="font-mono text-white" data-testid="checkout-entry-time">{formatDateTime(preview.entry_time)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#A1A1AA] text-sm">Salida (est.)</span>
                  <span className="font-mono text-white">{formatDateTime(preview.exit_time)}</span>
                </div>
                <div className="border-t border-[#27272A] pt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#A1A1AA]">
                    <Clock className="w-5 h-5" weight="bold" />
                    <span>Tiempo Total</span>
                  </div>
                  <span className="font-mono text-xl text-white" data-testid="checkout-duration">{formatDuration(preview.total_minutes)}</span>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-[#1C1C1E] rounded-lg p-4">
                <div className="space-y-2 mb-3">
                  {preview.breakdown?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-[#A1A1AA]">{item.description}</span>
                      <span className={`font-mono ${item.amount < 0 ? 'text-[#4ADE80]' : 'text-white'}`}>{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
                {paidAmount > 0 && (
                  <div className="text-xs text-[#A1A1AA] italic mb-3 pb-3 border-b border-[#27272A]">
                    * Cobrado por separado: {formatCurrency(paidAmount)}
                  </div>
                )}
                <div className="border-t border-[#27272A] pt-3 flex items-center justify-between">
                  <span className="text-lg text-white font-medium">TOTAL</span>
                  <span className="price-display text-[#4ADE80]" data-testid="checkout-total">{formatCurrency(preview.total_price)}</span>
                </div>
              </div>

              {/* Adjustments */}
              {!showAdjustments ? (
                <button onClick={() => setShowAdjustments(true)} className="w-full btn-secondary text-sm" data-testid="show-adjustments-btn">
                  Ajustes (Descuento / Cortesia)
                </button>
              ) : (
                <div className="bg-[#1C1C1E] rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Ajustes</span>
                    <button onClick={resetAdjustments} className="text-[#A1A1AA] text-sm hover:text-white">Restablecer</button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={handleDiscount} className="flex flex-col items-center gap-1 p-3 bg-[#27272A] rounded-lg hover:bg-[#3f3f46] transition-colors" data-testid="discount-btn">
                      <Percent className="w-6 h-6 text-[#FCD34D]" weight="bold" /><span className="text-xs text-[#A1A1AA]">Descuento</span>
                    </button>
                    <button onClick={handleCourtesy} className="flex flex-col items-center gap-1 p-3 bg-[#27272A] rounded-lg hover:bg-[#3f3f46] transition-colors" data-testid="courtesy-btn">
                      <Gift className="w-6 h-6 text-[#4ADE80]" weight="bold" /><span className="text-xs text-[#A1A1AA]">Cortesia</span>
                    </button>
                    <button onClick={handleCustomAmount} className="flex flex-col items-center gap-1 p-3 bg-[#27272A] rounded-lg hover:bg-[#3f3f46] transition-colors" data-testid="custom-amount-btn">
                      <CurrencyCircleDollar className="w-6 h-6 text-[#60A5FA]" weight="bold" /><span className="text-xs text-[#A1A1AA]">Monto</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#27272A] space-y-2">
          <button onClick={handleConfirm} disabled={confirming} className="w-full btn-success text-lg" data-testid="checkout-confirm">
            {confirming ? 'Procesando...' : <span className="flex items-center justify-center gap-2"><Receipt className="w-5 h-5" weight="bold" />Confirmar salida e imprimir</span>}
          </button>
          <button onClick={onClose} className="w-full btn-secondary" data-testid="checkout-cancel">Cancelar</button>
          <div className="pt-3 mt-3 border-t border-[#27272A]">
            <button onClick={() => setShowVoidConfirm(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#450A0A] hover:bg-[#7F1D1D] border border-[#DC2626] text-[#FCA5A5] rounded-lg transition-colors"
              data-testid="void-session-btn">
              <Trash className="w-5 h-5" weight="bold" /><span className="font-medium">Eliminar por error</span>
            </button>
            <p className="text-[#71717A] text-xs text-center mt-1">Solo usar para errores del operador</p>
          </div>
        </div>

        {/* Void Confirmation */}
        {showVoidConfirm && (
          <div className="absolute inset-0 bg-black/90 flex items-center justify-center p-4 rounded-xl">
            <div className="bg-[#1C1C1E] border border-[#DC2626] rounded-xl p-6 max-w-sm w-full">
              <div className="flex items-center gap-3 mb-4">
                <Warning className="w-8 h-8 text-[#FCA5A5]" weight="bold" />
                <h3 className="text-lg font-heading text-white">Eliminar esta sesion?</h3>
              </div>
              <p className="text-[#A1A1AA] text-sm mb-4">
                Esta accion es solo para errores del operador. La sesion sera eliminada y <strong className="text-white">NO contara</strong> en los totales ni reportes.
              </p>
              {preview && (
                <div className="bg-[#27272A] rounded-lg p-3 mb-4 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">Patente:</span><span className="text-white font-mono">{preview.plate_number}</span></div>
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">Tiempo:</span><span className="text-white">{formatDuration(preview.total_minutes)}</span></div>
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">Monto est.:</span><span className="text-[#FCA5A5]">{formatCurrency(preview.total_price)}</span></div>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setShowVoidConfirm(false)} className="flex-1 btn-secondary" data-testid="void-cancel-btn">Cancelar</button>
                <button onClick={handleVoidSession} disabled={voiding}
                  className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
                  data-testid="void-confirm-btn">
                  {voiding ? 'Eliminando...' : 'Si, Eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Charge Modal — 3 steps: product → quantity → mode */}
        {showAddCharge && (
          <div className="absolute inset-0 bg-black/90 flex items-center justify-center p-4 rounded-xl">
            <div className="bg-[#1C1C1E] border border-[#60A5FA]/30 rounded-xl p-6 max-w-sm w-full">

              {/* Step 1: Select product */}
              {chargeStep === 1 && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-heading text-white">Seleccionar producto</h3>
                    <button onClick={() => setShowAddCharge(false)} className="p-1 hover:bg-[#27272A] rounded-lg">
                      <X className="w-5 h-5 text-[#A1A1AA]" weight="bold" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {products.map((product) => (
                      <button key={product.id} onClick={() => handleSelectProduct(product)}
                        className="w-full flex items-center justify-between p-4 bg-[#27272A] rounded-lg hover:bg-[#3f3f46] transition-colors"
                        data-testid={`product-${product.name}`}>
                        <div className="flex flex-col items-start">
                          <span className="text-white font-medium">{product.name}</span>
                          <span className="text-xs text-[#A1A1AA]">{product.category === 'service' ? 'Servicio' : 'Producto'}</span>
                        </div>
                        <span className="text-[#4ADE80] font-mono text-lg">{formatCurrency(product.price)}</span>
                      </button>
                    ))}
                    {products.length === 0 && <p className="text-[#A1A1AA] text-sm text-center py-4">No hay productos</p>}
                  </div>
                </>
              )}

              {/* Step 2: Select quantity */}
              {chargeStep === 2 && selectedProduct && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-heading text-white">{selectedProduct.name}</h3>
                      <p className="text-[#A1A1AA] text-sm">{formatCurrency(selectedProduct.price)} c/u</p>
                    </div>
                    <button onClick={() => { setChargeStep(1); setSelectedProduct(null); }} className="text-[#A1A1AA] text-sm hover:text-white">
                      Cambiar
                    </button>
                  </div>

                  <p className="text-[#A1A1AA] text-sm mb-4">Cantidad:</p>

                  {/* Touch-friendly quantity selector */}
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="w-14 h-14 flex items-center justify-center bg-[#27272A] hover:bg-[#3f3f46] rounded-xl transition-colors"
                      data-testid="qty-minus-btn">
                      <Minus className="w-7 h-7 text-white" weight="bold" />
                    </button>
                    <div className="w-20 text-center">
                      <span className="text-4xl font-heading text-white" data-testid="qty-display">{quantity}</span>
                    </div>
                    <button onClick={() => setQuantity(q => q + 1)}
                      className="w-14 h-14 flex items-center justify-center bg-[#27272A] hover:bg-[#3f3f46] rounded-xl transition-colors"
                      data-testid="qty-plus-btn">
                      <Plus className="w-7 h-7 text-white" weight="bold" />
                    </button>
                  </div>

                  {/* Subtotal */}
                  <div className="text-center mb-6">
                    <span className="text-[#A1A1AA] text-sm">Subtotal: </span>
                    <span className="text-[#4ADE80] font-mono text-2xl" data-testid="qty-subtotal">
                      {formatCurrency(selectedProduct.price * quantity)}
                    </span>
                  </div>

                  <button onClick={() => setChargeStep(3)}
                    className="w-full btn-success py-3"
                    data-testid="qty-continue-btn">
                    Continuar
                  </button>
                </>
              )}

              {/* Step 3: Select payment mode */}
              {chargeStep === 3 && selectedProduct && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-heading text-white">
                        {selectedProduct.name} x{quantity}
                      </h3>
                      <p className="text-[#4ADE80] font-mono">{formatCurrency(selectedProduct.price * quantity)}</p>
                    </div>
                    <button onClick={() => setChargeStep(2)} className="text-[#A1A1AA] text-sm hover:text-white">
                      Cambiar
                    </button>
                  </div>
                  <p className="text-[#A1A1AA] text-sm mb-4">Modo de pago:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => handleSelectPaymentMode('paid_now')} disabled={chargeLoading}
                      className="flex flex-col items-center gap-2 p-4 bg-[#052E16] border border-[#16A34A] rounded-lg hover:bg-[#064E3B] transition-colors disabled:opacity-50"
                      data-testid="charge-paid-now-btn">
                      <CurrencyCircleDollar className="w-8 h-8 text-[#4ADE80]" weight="bold" />
                      <span className="text-[#4ADE80] text-sm font-medium text-center">Cobrar ahora</span>
                    </button>
                    <button onClick={() => handleSelectPaymentMode('add_to_total')} disabled={chargeLoading}
                      className="flex flex-col items-center gap-2 p-4 bg-[#422006] border border-[#D97706] rounded-lg hover:bg-[#4A2509] transition-colors disabled:opacity-50"
                      data-testid="charge-add-total-btn">
                      <Receipt className="w-8 h-8 text-[#FCD34D]" weight="bold" />
                      <span className="text-[#FCD34D] text-sm font-medium text-center">Agregar al total</span>
                    </button>
                  </div>
                  {chargeLoading && <p className="text-[#A1A1AA] text-sm text-center mt-3">Procesando...</p>}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
